import { Canvas, useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";

function Particles({ count = 900 }: { count?: number }) {
  const ref = useRef<THREE.Points>(null!);

  const { positions, scales } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const scales = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const r = Math.pow(Math.random(), 0.55) * 5.5;
      const theta = Math.random() * Math.PI * 2;
      const y = (Math.random() - 0.5) * 1.4;
      positions[i * 3] = Math.cos(theta) * r;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = Math.sin(theta) * r - 1;
      scales[i] = Math.random();
    }
    return { positions, scales };
  }, [count]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    ref.current.rotation.y = t * 0.05;
    const pos = ref.current.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < count; i++) {
      const ix = i * 3 + 1;
      pos.array[ix] = positions[ix] + Math.sin(t * 0.6 + i * 0.1) * 0.15;
    }
    pos.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-scale" args={[scales, 1]} />
      </bufferGeometry>
      <pointsMaterial
        size={0.055}
        color={new THREE.Color("#4A6B3E")}
        transparent
        opacity={0.55}
        sizeAttenuation
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function Orbs() {
  const group = useRef<THREE.Group>(null!);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    group.current.rotation.y = Math.sin(t * 0.15) * 0.3;
    group.current.children.forEach((c, i) => {
      c.position.y = Math.sin(t * 0.4 + i) * 0.2;
    });
  });
  const orbs = Array.from({ length: 7 }, (_, i) => {
    const angle = (i / 7) * Math.PI * 2;
    const r = 3 + (i % 2) * 0.6;
    return [Math.cos(angle) * r, 0, Math.sin(angle) * r - 1] as [number, number, number];
  });
  return (
    <group ref={group}>
      {orbs.map((p, i) => (
        <mesh key={i} position={p}>
          <sphereGeometry args={[0.55 + (i % 3) * 0.1, 32, 32]} />
          <meshStandardMaterial
            color="#9FB38E"
            transparent
            opacity={0.22}
            roughness={0.4}
            emissive="#4A6B3E"
            emissiveIntensity={0.2}
          />
        </mesh>
      ))}
    </group>
  );
}

export function HeroScene() {
  return (
    <Canvas
      className="!absolute inset-0"
      camera={{ position: [0, 0.3, 6], fov: 60 }}
      gl={{ antialias: true, alpha: true }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} color="#EFEDE2" />
      <pointLight position={[-4, 2, 3]} intensity={1} color="#A8B89A" />
      <Orbs />
      <Particles />
      <fog attach="fog" args={["#EFEDE2", 5, 12]} />
    </Canvas>
  );
}
